import React, { createContext, useContext, useState, ReactNode, PropsWithChildren, useEffect, useMemo, useCallback, useRef } from 'react';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

const PLACEHOLDER_USER_IDS = new Set(['no owner', 'user id', 'userid', 'uid']);

declare global {
  interface Window {
    companyID?: string;
    companyName?: string;
    companyPlan?: string;
    customerPlan?: string;
    customerPlanFriendly?: string;
    legalName?: string;
    activeTenancy?: Tenancy;
  }
}

export interface Tenancy {
  name: string;
  logo: string; // Changed from React.ElementType to string URL
  plan: string;
  id: string; // Added id property
  lastAccessed?: string; // ISO date string of when the tenancy was last accessed
  legalName?: string;
  tenancyOwner?: string;
  disabledFeatures?: string[];
  // MSP impersonation permissions (populated by TenancyContextProvider when available)
  impersonationAllowed?: boolean;
  impersonationAllowedBusiness?: boolean;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: Record<string, boolean>;
  // DED-605: Removed team field - Team Settings should not be in User Management
  businessUnit?: string;
  ismsRole?: string;
  ismsCommittee?: string;
}

interface TenancyContextType {
  activeTenancy: Tenancy | null;
  setActiveTenancy: (tenancy: Tenancy) => void;
  tenancies: Tenancy[];
  loading: boolean;
  users: User[];
  usersLoading: boolean;
  userIdToName: Record<string, string>;
  tenancyIdToName: Record<string, string>;
  currentUser: { [key: string]: any; userID: string };
  enabledModules: string[];
  /**
   * Check if a module is enabled
   * @param moduleName - The name of the module to check (e.g., 'ISO 27001' or 'iso27001')
   * @returns boolean - Whether the module is enabled
   */
  isModuleEnabled: (moduleName: string, dbName?: string) => boolean;
}

const TenancyContext = createContext<TenancyContextType>({
  activeTenancy: {
    name: 'Loading...',
    logo: '',
    plan: '',
    id: '',
  },
  setActiveTenancy: () => { },
  tenancies: [],
  loading: true,
  users: [],
  usersLoading: true,
  userIdToName: {},
  tenancyIdToName: {},
  currentUser: { userID: '' },
  enabledModules: [],
  isModuleEnabled: (moduleName: string, dbName?: string) => false, // Default to false when context is not available
}); // Default: no modules enabled

export function useTenancy() {
  return useContext(TenancyContext);
}

export function useUsers() {
  const { users, usersLoading } = useContext(TenancyContext);
  return { users, loading: usersLoading };
}

/**
 * Hook to get a mapping of tenancy IDs to tenancy names
 * 
 * @returns Record mapping tenancy IDs to their display names
 */
export const useTenancyMapping = () => {
  const { tenancyIdToName } = useTenancy();
  return tenancyIdToName;
};

/**
 * Hook to get the current tenancy name
 * 
 * @returns The name of the active tenancy
 */
export function useTenancyName() {
  const { activeTenancy } = useContext(TenancyContext);
  return activeTenancy?.name || 'Acme Inc';
}

/**
 * Hook to access the current logged-in user data
 * 
 * @returns The current user object with merged data from Firebase Auth and Firestore
 * @example
 * const currentUser = useCurrentUser();
 * 
 * if (currentUser) {
 *   console.log(`Logged in as ${currentUser.firstName} ${currentUser.lastName}`);
 *   console.log(`Email: ${currentUser.email}`);
 *   console.log(`Roles: ${Object.keys(currentUser.roles || {}).join(', ')}`);
 * }
 */
export const useCurrentUser = () => {
  const { currentUser } = useTenancy();
  return currentUser;
};

interface TenancyProviderProps {
  initialTenancy?: Tenancy;
  tenancies?: Tenancy[];
  loading?: boolean;
  users?: User[];
  usersLoading?: boolean;
  currentUser?: { userID: string;[key: string]: any };
  enabledModules?: string[];
  isModuleEnabled?: (moduleName: string) => boolean;
}

export function TenancyProvider({ children, initialTenancy, tenancies = [], loading = false, users = [], usersLoading = false, currentUser, enabledModules = [], isModuleEnabled = (moduleName: string, dbName?: string) => false }: PropsWithChildren<TenancyProviderProps>) {
  const [activeTenancy, setActiveTenancyState] = useState<Tenancy>(() => {
    // Try to load from localStorage first
    if (typeof window !== 'undefined') {
      try {
        const cached = localStorage.getItem('active_tenancy_cache_v1');
        if (cached) {
          const parsed = JSON.parse(cached);
          // Only use cached tenancy if it exists in our tenancies list
          if (tenancies.some(t => t.id === parsed.id)) {
            return {
              ...parsed,
              lastAccessed: parsed.lastAccessed || new Date().toISOString()
            };
          }
        }
      } catch (e) {
        console.error('Failed to parse cached tenancy:', e);
      }
    }

    // Fall back to initialTenancy or first tenancy
    const defaultTenancy = initialTenancy || tenancies[0] || {
      name: 'Loading...',
      logo: '',
      plan: 'Loading...',
      id: '',
      lastAccessed: new Date().toISOString()
    };

    return defaultTenancy;
  });

  // Keep track of all tenancies with their access times
  const [tenanciesWithAccess, setTenanciesWithAccess] = useState<Tenancy[]>(() => {
    return tenancies.map(t => ({
      ...t,
      lastAccessed: t.lastAccessed || new Date(0).toISOString() // Default to epoch if not set
    }));
  });

  // Set window.companyID whenever activeTenancy changes
  useEffect(() => {
    if (activeTenancy && activeTenancy.id) {
      window.companyID = activeTenancy.id;
      window.companyName = activeTenancy.name;
      window.companyPlan = activeTenancy.plan === 'Business Plan' ? 'iso' : 'starter';
      window.customerPlanFriendly = activeTenancy.plan === 'Business Plan' ? 'iso' : 'starter';
      window.customerPlan = activeTenancy.plan === 'Business Plan' ? undefined : 'starter';
      window.legalName = activeTenancy.legalName;
      window.activeTenancy = activeTenancy;
    } else {
      window.companyID = undefined;
      window.companyName = undefined;
      window.companyPlan = undefined;
      window.customerPlanFriendly = undefined;
      window.customerPlan = undefined;
      window.legalName = undefined;
      window.activeTenancy = undefined;
    }
  }, [activeTenancy]);

  // Keep activeTenancy in sync with initialTenancy if it changes (e.g., after cache/async load)
  useEffect(() => {
    if (
      initialTenancy &&
      (!activeTenancy || activeTenancy.id !== initialTenancy.id)
    ) {
      const setActiveTenancy = (tenancy: Tenancy) => {
        // Update the lastAccessed timestamp when switching tenancies
        const tenancyToActivate = { ...tenancy, lastAccessed: new Date().toISOString() };
        setActiveTenancyState(tenancyToActivate);

        // Update the tenancies array with the updated tenancy
        if (tenancies.some(t => t.id === tenancyToActivate.id)) {
          tenancies = tenancies.map(t =>
            t.id === tenancyToActivate.id ? tenancyToActivate : t
          );
        }

        // Update localStorage if available
        if (typeof window !== 'undefined') {
          localStorage.setItem('active_tenancy_cache_v1', JSON.stringify(tenancyToActivate));
        }
      };
      setActiveTenancy(initialTenancy);
    }
  }, [initialTenancy]);

  // Wrapped setter to update window.companyID and track lastAccessed
  const setActiveTenancy = (tenancy: Tenancy) => {
    const now = new Date().toISOString();
    const updatedTenancy = { ...tenancy, lastAccessed: now };

    // Update the active tenancy
    setActiveTenancyState(updatedTenancy);

    // Update the tenancies array with the new access time
    setTenanciesWithAccess(prev => {
      const exists = prev.some(t => t.id === tenancy.id);
      if (exists) {
        return prev.map(t => t.id === tenancy.id ? updatedTenancy : t);
      }
      return [...prev, updatedTenancy];
    });

    // Update window properties
    if (tenancy && tenancy.id) {
      window.companyID = tenancy.id;
      window.companyName = tenancy.name;
      const isIsoPlan = tenancy.plan === 'Business Plan' || tenancy.plan === 'Enterprise Plan';
      window.companyPlan = isIsoPlan ? 'iso' : 'starter';
      window.customerPlanFriendly = isIsoPlan ? 'iso' : 'starter';
      window.customerPlan = isIsoPlan ? undefined : 'starter';
      window.legalName = tenancy.legalName;
    } else {
      window.companyID = undefined;
      window.companyName = undefined;
      window.companyPlan = undefined;
      window.customerPlanFriendly = undefined;
      window.customerPlan = undefined;
      window.legalName = undefined;
    }

    // Update localStorage if available
    if (typeof window !== 'undefined') {
      localStorage.setItem('active_tenancy_cache_v1', JSON.stringify(updatedTenancy));
    }
  };

  // Merge the original tenancies with our access tracking
  const mergedTenancies = tenancies.map(t => {
    const accessed = tenanciesWithAccess.find(at => at.id === t.id);
    return accessed ? { ...t, lastAccessed: accessed.lastAccessed } : t;
  });

  // Create hook for getting current user details from Firebase Auth
  const [currentUserDetails, setCurrentUserDetails] = useState(currentUser);

  const tenancyIdToName = useMemo(() => {
    return tenancies.reduce((acc, t) => {
      acc[t.id] = t.name;
      return acc;
    }, {} as Record<string, string>);
  }, [tenancies]);

  const baseUserNameMap = useMemo(() => {
    const initialMap = users.reduce((acc, u) => {
      acc[u.id] = `${u.firstName} ${u.lastName}`.trim();
      return acc;
    }, {} as Record<string, string>);

    if (currentUserDetails?.userID) {
      initialMap[currentUserDetails.userID] = `${currentUserDetails.firstName ?? ''} ${currentUserDetails.lastName ?? ''}`.trim();
    }

    return initialMap;
  }, [users, currentUserDetails]);

  const [userNameCache, setUserNameCache] = useState<Record<string, string>>(baseUserNameMap);
  const pendingLookupsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    setUserNameCache(prev => ({ ...prev, ...baseUserNameMap }));
  }, [baseUserNameMap]);

  const resolveUserId = useCallback((userId: string): string => {
    const normalised = userId?.trim().toLowerCase();
    if (normalised && PLACEHOLDER_USER_IDS.has(normalised)) {
      const fallbackId = activeTenancy?.tenancyOwner || currentUserDetails?.userID;
      return fallbackId || userId;
    }
    return userId;
  }, [activeTenancy?.tenancyOwner, currentUserDetails?.userID]);

  const fetchUserNameById = useCallback(async (rawUserId: string) => {
    const userId = resolveUserId(rawUserId);
    if (!userId || pendingLookupsRef.current.has(userId)) {
      return;
    }

    if (!activeTenancy?.id) {
      return;
    }

    pendingLookupsRef.current.add(userId);
    try {
      const response = await deiterateAPI.POST<{ firstName?: string; lastName?: string; isMsp?: boolean }>(
        '/deiterateInternalAPI/users/profile',
        {
          body: JSON.stringify({ userID: userId }),
          companyID: activeTenancy.id,
        }
      ).catch(async error => {
        const message = (error as Error)?.message ?? '';
        const trimmed = message.trim();
        if (trimmed.includes('404') || trimmed.toLowerCase().includes('not found')) {
          return { firstName: 'Deleted', lastName: 'User', isMsp: false };
        }
        throw error;
      });

      if (response && (response.firstName || response.lastName)) {
        const nameParts = [response.firstName, response.lastName].filter(Boolean);
        const fullName = nameParts.join(' ').trim();
        const label = response.isMsp && fullName ? `${fullName} (MSP)` : fullName || userId;

        setUserNameCache(prev => {
          if (prev[userId] === label) {
            return prev;
          }
          return {
            ...prev,
            [userId]: label,
          };
        });
      }
    } catch (error) {
      console.error('[TenancyContext] Failed to load user profile for', userId, error);
    } finally {
      pendingLookupsRef.current.delete(userId);
    }
  }, [activeTenancy?.id, resolveUserId]);

  const userIdToName = useMemo(() => {
    return new Proxy(userNameCache, {
      get(target, prop, receiver) {
        if (typeof prop !== 'string') {
          return Reflect.get(target, prop, receiver);
        }

        const resolvedId = resolveUserId(prop);
        const cachedValue = resolvedId !== prop ? target[resolvedId] : target[prop];

        if (!cachedValue) {
          void fetchUserNameById(prop);
          if (resolvedId !== prop) {
            void fetchUserNameById(resolvedId);
          }
        }

        if (resolvedId !== prop && cachedValue) {
          setUserNameCache(prev => (
            prev[prop] === cachedValue
              ? prev
              : {
                ...prev,
                [prop]: cachedValue,
              }
          ));
          return cachedValue;
        }

        return cachedValue || resolvedId;
      },
    });
  }, [userNameCache, fetchUserNameById, resolveUserId]);

  // Update current user details when the prop changes
  useEffect(() => {
    if (currentUser) {
      setCurrentUserDetails(currentUser);
    }
  }, [currentUser]);

  // Add hook to update user data from Firestore
  useEffect(() => {
    // Skip if no currentUser or no activeTenancy
    if (!currentUserDetails?.userID || !activeTenancy?.id) return;

    // This could be enhanced to fetch additional user details from Firestore
    const fetchCurrentUserDetails = async () => {
      try {
        // Find the current user in the users array
        const userDetails = users.find(u => u.id === currentUserDetails.userID);

        if (userDetails) {
          // Merge Firebase auth user with Firestore user data
          // DED-605: Removed team - Team Settings should not be in User Management
          setCurrentUserDetails(prevUser => ({
            ...prevUser,
            firstName: userDetails.firstName,
            lastName: userDetails.lastName,
            roles: userDetails.roles,
            businessUnit: userDetails.businessUnit,
            ismsRole: userDetails.ismsRole,
            ismsCommittee: userDetails.ismsCommittee
          }));
          window.currentUser = currentUserDetails;
        }
      } catch (error) {
        console.error('Error fetching current user details:', error);
      }
    };

    fetchCurrentUserDetails();
  }, [currentUserDetails?.userID, activeTenancy?.id, users]);

  const value = {
    activeTenancy,
    setActiveTenancy,
    tenancies: mergedTenancies,
    loading,
    tenancyIdToName,
    userIdToName,
    users,
    usersLoading,
    currentUser: currentUserDetails,
    enabledModules,
    isModuleEnabled
  };

  // Check if is essential-eight route and set active tenancy to Demo Tenant
  useEffect(() => {
    if (window.location.pathname === '/essential-eight') {
      setActiveTenancy({
        name: 'Essential Eight Tenancy',
        logo: '/assets/ducks/blank.svg',
        plan: 'Business Plan',
        id: 'demo-tenant',
        lastAccessed: new Date().toISOString()
      });
    }
  }, []);

  return <TenancyContext.Provider value={value}>{children}</TenancyContext.Provider>;
}

export { useRegisterItems, useKeyDocuments, useOrgStructure, loadCachedUsers, saveCachedUsers } from './TenancyContextProvider';
